// Series fill — what dragging the handle should actually do.
//
// Tiling the source block is wrong for the case people reach for it: drag 1,2,3
// and Excel gives 4,5,6, not 1,2,3,1,2,3. The step is whatever the source shows
// — 2,4,6 continues 8,10,12 — and dates step by day or by month, whichever the
// source is actually doing.
//
// Where no series can be read (mixed text, an irregular gap), the fill falls
// back to tiling, which is also what Excel does.

import type { CellValue, FormatId } from './types';
import { addMonths, isPlausibleSerial, monthsBetween } from './dates';

export interface SeriesCell {
  value: CellValue;
  fmt?: FormatId;
}

export interface Series {
  kind: 'number' | 'day' | 'month' | 'text';
  /**
   * The value `k` steps beyond the source block — k = 1 is the first filled
   * cell. When `reverse`, k = 1 is the first cell *before* the block.
   */
  at(k: number): SeriesCell;
}

const DATE_FORMATS: ReadonlySet<FormatId> = new Set(['date', 'dateLong', 'monthYear']);

export function isDateCell(cell: SeriesCell | undefined): boolean {
  return (
    cell !== undefined &&
    typeof cell.value === 'number' &&
    cell.fmt !== undefined &&
    DATE_FORMATS.has(cell.fmt) &&
    isPlausibleSerial(cell.value)
  );
}

/**
 * Read a series out of the source cells.
 *
 * `reverse` means the drag went up or left, so the series extends backwards
 * from the FIRST cell rather than forwards from the last — dragging a column of
 * dates upward should reach earlier months, not repeat later ones.
 */
export function detectSeries(cells: readonly SeriesCell[], reverse = false): Series | null {
  const filled = cells.filter((c) => c.value !== null && c.value !== undefined && c.value !== '');
  if (filled.length === 0) return null;

  const dates = detectDateSeries(filled, reverse);
  if (dates) return dates;

  const numbers = detectNumberSeries(filled, reverse);
  if (numbers) return numbers;

  return detectTextSeries(filled, reverse);
}

function anchor<T>(list: readonly T[], reverse: boolean): T {
  return reverse ? list[0]! : list[list.length - 1]!;
}

/** Every step is the same, and there is at least one. */
function constantStep(values: readonly number[]): number | null {
  if (values.length < 2) return null;
  const step = values[1]! - values[0]!;
  for (let i = 2; i < values.length; i++) {
    if (values[i]! - values[i - 1]! !== step) return null;
  }
  return step;
}

function detectDateSeries(cells: readonly SeriesCell[], reverse: boolean): Series | null {
  if (!cells.every(isDateCell)) return null;
  const serials = cells.map((c) => c.value as number);
  const fmt = anchor(cells, reverse).fmt;
  const base = anchor(serials, reverse);
  const sign = reverse ? -1 : 1;

  // A single date steps by one day, as Excel does.
  if (serials.length === 1) {
    return { kind: 'day', at: (k) => ({ value: base + sign * k, fmt }) };
  }

  // Whole months first: 1 Jan, 1 Feb, 1 Mar is a monthly series, and continuing
  // it by its 31-day gap would drift off the first of the month immediately.
  //
  // Months are measured from the FIRST value, never pairwise. 31 Jan -> 28 Feb
  // -> 31 Mar is a monthly series, but pairwise it looks broken, because
  // 28 Feb + 1 month clamps to 28 Mar. Anchoring on the first value is also
  // what makes the continuation right: from 31 Jan, +3 is 30 Apr, not 28 Apr.
  const first = serials[0]!;
  const offsets: number[] = [];
  for (const serial of serials) {
    const months = monthsBetween(first, serial);
    if (months === null) {
      offsets.length = 0;
      break;
    }
    offsets.push(months);
  }
  const monthStep = offsets.length === serials.length ? constantStep(offsets) : null;
  if (monthStep !== null && monthStep !== 0) {
    const lastIndex = serials.length - 1;
    return {
      kind: 'month',
      at: (k) => ({
        value: addMonths(first, monthStep * (reverse ? -k : lastIndex + k)),
        fmt,
      }),
    };
  }

  const dayStep = constantStep(serials);
  if (dayStep !== null && dayStep !== 0) {
    return { kind: 'day', at: (k) => ({ value: base + sign * dayStep * k, fmt }) };
  }
  return null;
}

function detectNumberSeries(cells: readonly SeriesCell[], reverse: boolean): Series | null {
  if (!cells.every((c) => typeof c.value === 'number' && Number.isFinite(c.value))) return null;
  // A single number COPIES in Excel — only two or more establish a step. Making
  // one number count up would break every "same rate down the column" fill.
  if (cells.length < 2) return null;
  const values = cells.map((c) => c.value as number);
  const step = constantStep(values);
  if (step === null || step === 0) return null;
  const base = anchor(values, reverse);
  const fmt = anchor(cells, reverse).fmt;
  const sign = reverse ? -1 : 1;
  return { kind: 'number', at: (k) => ({ value: base + sign * step * k, fmt }) };
}

const TRAILING_NUMBER = /^(.*?)(\d+)(\D*)$/;

function detectTextSeries(cells: readonly SeriesCell[], reverse: boolean): Series | null {
  if (!cells.every((c) => typeof c.value === 'string')) return null;
  const parsed = cells.map((c) => TRAILING_NUMBER.exec(c.value as string));
  if (parsed.some((m) => m === null)) return null;

  const prefix = parsed[0]![1]!;
  const suffix = parsed[0]![3]!;
  if (parsed.some((m) => m![1] !== prefix || m![3] !== suffix)) return null;

  const numbers = parsed.map((m) => Number(m![2]));
  // Preserve zero padding: "Unit 001" continues "Unit 002", not "Unit 2".
  const width = parsed[0]![2]!.length;
  const padded = parsed.every((m) => m![2]!.length === width) && parsed[0]![2]!.startsWith('0');

  const step = cells.length === 1 ? 1 : constantStep(numbers);
  if (step === null || step === 0) return null;
  const base = anchor(numbers, reverse);
  const fmt = anchor(cells, reverse).fmt;
  const sign = reverse ? -1 : 1;

  return {
    kind: 'text',
    at: (k) => {
      const n = base + sign * step * k;
      const digits = padded ? String(Math.abs(n)).padStart(width, '0') : String(Math.abs(n));
      return { value: `${prefix}${n < 0 ? '-' : ''}${digits}${suffix}`, fmt };
    },
  };
}
